import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { ArtistRelativeScore, NodeDailyHeat, SeekerCheckinRanking } from '../types/ranking';

interface UseRankingDataReturn {
  artists: ArtistRelativeScore[];
  nodes: NodeDailyHeat[];
  seekers: SeekerCheckinRanking[];
  loading: boolean;
  error: string | null;
}

export const useRankingData = (prefecture?: string): UseRankingDataReturn => {
  const [artists, setArtists] = useState<ArtistRelativeScore[]>([]);
  const [nodes, setNodes] = useState<NodeDailyHeat[]>([]);
  const [seekers, setSeekers] = useState<SeekerCheckinRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRankingData = async () => {
      try {
        setLoading(true);
        setError(null);

        // アーティストランキングを取得
        let artistQuery = supabase
          .from('artist_relative_scores')
          .select('*');
        
        if (prefecture) {
          artistQuery = artistQuery.eq('prefecture', prefecture);
        }

        const { data: artistData, error: artistError } = await artistQuery;

        if (artistError) throw artistError;

        // NODEランキングを取得
        let nodeQuery = supabase
          .from('node_daily_heats')
          .select('*');
        
        if (prefecture) {
          nodeQuery = nodeQuery.eq('prefecture', prefecture);
        }

        const { data: nodeData, error: nodeError } = await nodeQuery;

        if (nodeError) throw nodeError;

        // SEEKERランキングを取得
        let seekerQuery = supabase
          .from('seeker_checkin_rankings')
          .select('*');
        
        if (prefecture) {
          seekerQuery = seekerQuery.eq('prefecture', prefecture);
        }

        const { data: seekerData, error: seekerError } = await seekerQuery;

        if (seekerError) throw seekerError;

        setArtists(artistData || []);
        setNodes(nodeData || []);
        setSeekers(seekerData || []);
      } catch (err) {
        console.error('ランキングデータの取得に失敗しました:', err);
        setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchRankingData();
  }, [prefecture]);

  return {
    artists,
    nodes,
    seekers,
    loading,
    error,
  };
};